import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Card,
  CardContent,
  Typography,
} from "@atlas/core";
import type { ICompanyApplicationTenant } from "@models";
import type { IAppLaunchRequest } from "@query";
import { getSolutionsAppMapId } from "@shared";
import { useEffect, useMemo, useState } from "react";
import { useHomeApplicationSolutions } from "../hooks/useHomeApplicationSolutions";
import { ApplicationLaunchCard } from "./ApplicationLaunchCard";
import { HomeSolutionsPagination } from "./HomeSolutionsPagination";
import {
  FIRST_PAGE_SOLUTIONS,
  getApplicationSolutionsPageCount,
  getApplicationSolutionsPageSlice,
  getHomeLaunchpadAppKey,
  HOME_SOLUTION_SKELETON_KEYS,
  HOME_SOLUTIONS_GRID_CLASS,
} from "./homeSolutions.constants";
import { SolutionLaunchCard } from "./SolutionLaunchCard";

interface IApplicationSolutionsSectionProps {
  appTenant: ICompanyApplicationTenant;
  companyUuid: string;
  solutionsFetchEnabled: boolean;
  searchQuery: string;
  isLaunchPending: boolean;
  launchVariables?: IAppLaunchRequest;
  onFilteredCountChange: (appKey: string, count: number) => void;
  onLaunchApp: (
    launchUrl: string,
    solutionId?: string,
    appMapId?: string,
  ) => void;
  userType: string | undefined;
  userName: string;
  userEmail: string;
  companyName: string;
  isInternalUser: boolean;
  isIRMDomain: boolean;
}

export function ApplicationSolutionsSection({
  appTenant,
  companyUuid,
  solutionsFetchEnabled,
  searchQuery,
  isLaunchPending,
  launchVariables,
  onFilteredCountChange,
  onLaunchApp,
  userType,
  userName,
  userEmail,
  companyName,
  isInternalUser,
  isIRMDomain,
}: IApplicationSolutionsSectionProps) {
  const [pageIndex, setPageIndex] = useState(0);

  const appKey = getHomeLaunchpadAppKey(appTenant);
  const appMapId = getSolutionsAppMapId(appTenant);
  const endpoint = appTenant.tenantInfo?.[0]?.connEndpoint ?? "";
  const canLaunch = !!appTenant.tenantInfo?.length && !!endpoint;
  const title = appTenant.applicationLabel || appTenant.applicationName || "";

  const { solutions: activeSolutions, isLoading: solutionsLoading } =
    useHomeApplicationSolutions({
      companyUuid,
      appMapId,
      // Temp fix: DXP Lite apps have no solutions; the children/solutions API fails for them.
      enabled:
        solutionsFetchEnabled &&
        !!companyUuid &&
        !!appMapId &&
        !appTenant.isLite,
    });

  const lowerQuery = searchQuery.trim().toLowerCase();

  // Reset page when search changes (avoid effect; adjust during render).
  const [pageResetQuery, setPageResetQuery] = useState(lowerQuery);
  if (lowerQuery !== pageResetQuery) {
    setPageResetQuery(lowerQuery);
    setPageIndex(0);
  }

  const appNameMatches =
    !lowerQuery || title.toLowerCase().includes(lowerQuery);

  const filteredSolutions = useMemo(() => {
    if (!lowerQuery || appNameMatches) return activeSolutions;
    return activeSolutions.filter((solution) => {
      const solutionTitle = solution.label || solution.name || "";
      return solutionTitle.toLowerCase().includes(lowerQuery);
    });
  }, [activeSolutions, appNameMatches, lowerQuery]);

  const isVisible =
    !lowerQuery ||
    appNameMatches ||
    solutionsLoading ||
    filteredSolutions.length > 0;

  const solutionCount = filteredSolutions.length;
  const pageCount = getApplicationSolutionsPageCount(solutionCount);
  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  const isFirstPage = safePageIndex === 0;
  const { start: pageStart, end: pageEnd } = getApplicationSolutionsPageSlice(
    solutionCount,
    safePageIndex,
  );
  const paginatedSolutions = useMemo(
    () => filteredSolutions.slice(pageStart, pageEnd),
    [filteredSolutions, pageStart, pageEnd],
  );

  // Include the always-present application card so app-name-only search matches
  // do not collapse into the empty search state.
  const reportedCount = isVisible ? filteredSolutions.length + 1 : 0;

  useEffect(() => {
    onFilteredCountChange(appKey, reportedCount);
  }, [appKey, reportedCount, onFilteredCountChange]);

  if (!isVisible) {
    return null;
  }

  const showPagination = solutionCount > FIRST_PAGE_SOLUTIONS;
  const rangeFrom = solutionCount === 0 ? 0 : pageStart + 1;
  const rangeTo = pageEnd;

  const isApplicationLaunching =
    isLaunchPending &&
    launchVariables?.launchUrl === endpoint &&
    !launchVariables?.solutionId &&
    launchVariables?.appMapId === appMapId;

  return (
    <AccordionItem value={appKey}>
      {/* Pagination must stay outside AccordionTrigger — trigger is a <button>,
          and nesting an input inside it collapses the section on focus/click. */}
      <div className="flex w-full min-w-0 items-center gap-3">
        <div className="min-w-0 flex-1">
          <AccordionTrigger>
            <Typography.H4 className="truncate text-base">
              {solutionsLoading ? title : `${title} (${solutionCount})`}
            </Typography.H4>
          </AccordionTrigger>
        </div>
        {showPagination ? (
          <div className="shrink-0 pr-2">
            <HomeSolutionsPagination
              totalCount={solutionCount}
              pageIndex={safePageIndex}
              pageCount={pageCount}
              rangeFrom={rangeFrom}
              rangeTo={rangeTo}
              onPageChange={setPageIndex}
              isLoading={solutionsLoading}
            />
          </div>
        ) : null}
      </div>

      <AccordionContent>
        <div className={HOME_SOLUTIONS_GRID_CLASS}>
          {isFirstPage ? (
            <ApplicationLaunchCard
              title={title}
              description={appTenant.applicationDescription}
              launchUrl={endpoint}
              appMapId={appMapId}
              launchDisabled={isLaunchPending || !canLaunch}
              isLaunching={isApplicationLaunching}
              onLaunchApp={onLaunchApp}
              termsAccepted={appTenant.termsAccepted ?? false}
              termsId={appTenant.termsId ?? ""}
              applicationId={appTenant.applicationId}
              companyUuid={companyUuid}
              userType={userType}
              userName={userName}
              userEmail={userEmail}
              companyName={companyName}
              isInternalUser={isInternalUser}
              isLiteApplication={appTenant.isLite ?? false}
              label={appTenant.applicationLabel}
              isIRMDomain={isIRMDomain}
              isTermsRequired={appTenant.dxpTermsRequired ?? false}
            />
          ) : null}

          {solutionsLoading
            ? HOME_SOLUTION_SKELETON_KEYS.map((skeletonKey) => (
                <Card key={skeletonKey} isLoading className="h-[246px] w-full">
                  <CardContent />
                </Card>
              ))
            : paginatedSolutions.map((solution) => (
                <SolutionLaunchCard
                  key={`sol-${appKey}-${solution.solutionId}`}
                  solutionData={solution}
                  title={solution.label || solution.name}
                  launchUrl={endpoint}
                  launchDisabled={isLaunchPending || !canLaunch}
                  isLaunching={
                    isLaunchPending &&
                    launchVariables?.launchUrl === endpoint &&
                    launchVariables?.solutionId === solution.solutionId
                  }
                  onLaunchApp={onLaunchApp}
                />
              ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
